import React, { useState, useEffect, useRef } from 'react';
import { Navbar, Nav, Row, Col, Button, Form, OverlayTrigger, Tooltip, Badge } from 'react-bootstrap';
import { columnManager } from '../Common/ColumnManager';
import PlayerCriteria from './PlayerCriteria';
import NumericalCriteria from './NumericalCriteria';
import DisplayStats from './DisplayStats';
import ExcelExporter from '../Common/ExcelExporter';
import PDFExporter from '../Common/PDFExporter';
import { GetQueryToolPlayerData, GetQueryToolContractsData, GetPlayers } from '../../Services/ApiSvc';
import TabulatorTable from '../Common/TabulatorTable';
import MultiSelect from '../Common/MultiSelect';
import IntRangeDropdown from '../Common/IntRangeDropdown';
import SeasonDropDown from '../Common/SeasonDropDown';
import SaveLoadButtons from '../Common/SaveLoadButtons';
import PlayerSearch from '../Common/PlayerSearch';
import { customDateString } from '../Common/CommonFunctions';
import { useStateWithCallbackLazy } from '../Common/CustomHooks';
import AdvancedFilters from '../DragAndDrop/AdvancedFilters';
import {
    insertIntoBlock,
    removeFromTree,
    updateLogOpInTree,
    deepCopyWithNewIds,
    getGranularitiesFromNodes,
    generateId,
    serializeFilterTree,
    deriveFilterLabel,
    deserializeFilterString,
    handleAfterDrop,
} from '../DragAndDrop/filterFunctions';

function QueryTool() {
    const [allColumns, setAllColumns] = useState([]);
    const [allPlayers, setAllPlayers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [defaultGroups, setDefaultGroups] = useState([]);
    const [selectableStatsByGroup, setSelectableStatsByGroup] = useState([]);
    const [selectableStatGroups, setSelectableStatGroups] = useState([]);

    // Form inputs from tab unique section
    const [header, setHeader] = useState(<h1>Query - Find Players Meeting Criteria in a Season</h1>);
    const [tabName, setTabName] = useState('season');
    const [consecutiveYears, setConsecutiveYears] = useState(1);
    const [consMinSeason, setConsMinSeason] = useState(2002);
    const [consMaxSeason, setConsMaxSeason] = useState((new Date()).getFullYear());
    const [throughX, setThroughX] = useState({ category: 'Any' });
    const [dateRanges, setDateRanges] = useState([{ startDate: new Date(2002, 1, 1), endDate: new Date() }]);
    const [playerSeasons, setPlayerSeasons] = useState([]);

    //const [playerType, setPlayerType] = useState('Hitter');
    const [hideFilters, setHideFilters] = useState(true);

    // Form inputs
    const [selectedSeasons, setSelectedSeasons] = useState([]);
    const [playerCriteria, setPlayerCriteria] = useState([]);
    const [numericalCriteria, setNumericalCriteria] = useStateWithCallbackLazy([]);
    const [displayStats, setDisplayStats] = useStateWithCallbackLazy([]);
    const [scaleUpFilters, setScaleUpFilters] = useState(false);
    const [scaleUpDisplayStats, setScaleUpDisplayStats] = useState(0);
    const [displayScaleUpOptions, setDisplayScaleUpOptions] = useState(false);
    const [usingScaleUp, setUsingScaleUp] = useState(false);
    const [cols, setCols] = useState([]);
    const [notes, setNotes] = useState({
        timePeriodNotes: [],
        playerSeasonNotes: [],
        playerCriteriaNotes: [],
        numericalCriteriaNotes: [],
        displayStatNotes: [],
        consecutiveYearsNote: '',
        throughXStatNotes: '',
        scaleUpNotes: ''
    });
    const aggregationValues = {
        none: 0,
        average: 1,
        totals: 2,
        both: 3
    }
    const [displayAggregationOptions, setDisplayAggregationOptions] = useState(false);
    const [aggregationOptions, setAggregationOptions] = useState(aggregationValues.both);

    // data handling
    const [data, setData] = useState([]);
    const [dataRowCount, setDataRowCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {

        (async () => {
            function adjustStatIds(statId) {
                let retStatId = statId > 10000 ? statId - 10000 : statId;
                // 73 and 74 are "next year" stats which are pointless in QueryTool because of time periods.
                return retStatId === 73
                    ? 730
                    : retStatId === 74
                        ? 731
                    : retStatId === 783
                        ? 562
                        : retStatId;
            }

            let allColumnsFromAPI = await columnManager.getAllColumns();
            let colGroupsFromAPI = await columnManager.getAllGroupsBySection('QueryTool');
            let defaultGroupsFromAPI = [];
            defaultGroupsFromAPI.push((await columnManager.getColIdsFromGroup(1))
                .map(adjustStatIds));
            defaultGroupsFromAPI.push((await columnManager.getColIdsFromGroup(2))
                .map(adjustStatIds));
            defaultGroupsFromAPI.push((await columnManager.getColIdsFromGroup(3))
                .map(adjustStatIds));
            defaultGroupsFromAPI.push((await columnManager.getColIdsFromGroup(4))
                .map(adjustStatIds));
            defaultGroupsFromAPI.push((await columnManager.getColIdsFromGroup(48))
                .map(adjustStatIds));
            let allPlayersFromAPI = (await GetPlayers()).filter(player => !player.transactionOnly);

            setAllColumns(allColumnsFromAPI);
            setGroups(colGroupsFromAPI);
            setDefaultGroups(defaultGroupsFromAPI);
            setAllPlayers(allPlayersFromAPI);
        })();
        
    }, [])

    useEffect(() => {
        if (groups.length < 1) {
            return;
        }
        let columnsForAutoComplete = groups.map(group => {
            return group.columns.map(col => {
                let posType;
                switch (col.statPosType) {
                    case 'b':
                        posType = 'Batting';
                        break;
                    case 'p':
                        posType = 'Pitching';
                        break;
                    case 'f':
                        posType = 'Fielding';
                        break;
                    default:
                        posType = '';
                        break;
                }
                return {
                    key: col.id,
                    title: col.title,
                    groupId: group.id,
                    formInputs: col.formInputs,
                    posType: posType,
                };
            })
        })
        setSelectableStatGroups(groups);
        setSelectableStatsByGroup(columnsForAutoComplete);
    }, [groups])

    useEffect(() => {
        if (consMaxSeason - consMinSeason + 1 < consecutiveYears) {
            setConsecutiveYears(consMaxSeason - consMinSeason + 1 + '');
        }
    }, [consMinSeason, consMaxSeason])

    useEffect(() => {
        if (tabName === 'consecutive')
            setThroughX({ category: 'Any' });
    }, [tabName])

    // ---------------------------------------------------------------------------
    // Filter state helpers
    // ---------------------------------------------------------------------------

    function applyFilterChange(statArray, statIndex, filterType, transform) {
        return statArray.map(stat => {
            if (stat.index !== statIndex) return stat;
            const key = filterType === 'ISW' ? 'iswFilters' : 'igwFilters';
            return { ...stat, [key]: transform(stat[key] || []) };
        });
    }

    function handleNumCritFilterDrop(filterNode, statIndex, filterType, targetBlockId) {
        setNumericalCriteria(prev =>
            applyFilterChange(prev, statIndex, filterType, nodes =>
                insertIntoBlock(nodes, targetBlockId, filterNode)
            )
        );
        handleAfterDrop();
    }

    function handleNumCritFilterRemove(nodeId, statIndex, filterType) {
        setNumericalCriteria(prev =>
            applyFilterChange(prev, statIndex, filterType, nodes =>
                removeFromTree(nodes, nodeId)
            )
        );
    }

    function handleNumCritLogOpChange(nodeId, newOp, statIndex, filterType) {
        setNumericalCriteria(prev =>
            applyFilterChange(prev, statIndex, filterType, nodes =>
                updateLogOpInTree(nodes, nodeId, newOp)
            )
        );
    }

    function handleDisStatFilterDrop(filterNode, statIndex, filterType, targetBlockId) {
        setDisplayStats(prev =>
            applyFilterChange(prev, statIndex, filterType, nodes =>
                insertIntoBlock(nodes, targetBlockId, filterNode)
            )
        );
        handleAfterDrop();
    }

    function handleDisStatFilterRemove(nodeId, statIndex, filterType) {
        setDisplayStats(prev =>
            applyFilterChange(prev, statIndex, filterType, nodes =>
                removeFromTree(nodes, nodeId)
            )
        );
    }

    function handleDisStatLogOpChange(nodeId, newOp, statIndex, filterType) {
        setDisplayStats(prev =>
            applyFilterChange(prev, statIndex, filterType, nodes =>
                updateLogOpInTree(nodes, nodeId, newOp)
            )
        );
    }

    function handleApplyToAll(filterNodes, section) {
        if (!filterNodes || filterNodes.length === 0) return;
        const filterType = filterNodes.find(n => n.kind === 'block')?.filterType;
        if (!filterType) return;

        function granularityOk(col, filterGranularities) {
            if (filterType === 'ISW') {
                const dropZoneGranularity = col.granularity ?? -1;
                return filterGranularities
                    .map(g => parseInt(g))
                    .every(g => g >= parseInt(dropZoneGranularity));
            } else {
                return filterGranularities
                    .map(g => parseInt(g))
                    .every(g => g <= 2);
            }
        }

        function statIsEligible(stat) {
            const col = stat.column?.formInputs;
            if (!col) return false;
            const filterableKey = filterType === 'ISW'
                ? (section === 'NumCrit' ? 'numCritISWFilterable' : 'disStatISWFilterable')
                : (section === 'NumCrit' ? 'numCritIGWFilterable' : 'disStatIGWFilterable');
            if (!col[filterableKey]) return false;
            const filterGranularities = getGranularitiesFromNodes(filterNodes);
            return granularityOk(col, filterGranularities);
        }

        function addFiltersToStat(stat) {
            if (!statIsEligible(stat)) return stat;
            const key = filterType === 'ISW' ? 'iswFilters' : 'igwFilters';
            const existing = stat[key] || [];
            const copied = deepCopyWithNewIds(filterNodes);
            const newFilters = existing.length > 0
                ? [...existing, { kind: 'logOp', id: generateId(), operator: 'AND' }, ...copied]
                : copied;
            return { ...stat, [key]: newFilters };
        }

        if (section === 'NumCrit') {
            setNumericalCriteria(prev => prev.map(addFiltersToStat));
        } else {
            setDisplayStats(prev => prev.map(addFiltersToStat));
        }
    }

    async function reset(curTabName) {
        setTabName(curTabName);
        setConsecutiveYears(1);
        setConsMinSeason(2002);
        setConsMaxSeason((new Date()).getFullYear());
        setDateRanges([{ startDate: new Date(2002, 1, 1), endDate: new Date() }]);
        setPlayerSeasons(['players'].includes(curTabName) ? [{ playerId: 0, platformYear: 0 }] : []);
        setSelectedSeasons([]);
        setPlayerCriteria([]);
        setNumericalCriteria([]);
        setCols([]);
        setNotes({
            timePeriodNotes: [],
            playerSeasonNotes: [],
            playerCriteriaNotes: [],
            numericalCriteriaNotes: [],
            displayStatNotes: [],
            consecutiveYearsNote: '',
            throughXStatNotes: '',
            scaleUpNotes: ''
        });
        setData([]);

        let disStatIds = [1];
        switch (curTabName) {
            case 'season':
                setHeader(<h1>Query - Find Players Meeting Criteria in a Season</h1>)
                setDisplayScaleUpOptions(true);
                disStatIds.push(2);
                break;
            case 'consecutive':
                setHeader(<h1>Query - Find Players Meeting Criteria in Consecutive Years</h1>)
                setThroughX({ category: 'Any' });
                setDisplayScaleUpOptions(true);
                disStatIds.push(2);
                break;
            case 'throughX':
                setHeader(<h1>Query - Find Players Meeting Criteria Through X</h1>)
                setThroughX({ category: 'Stat', value: 'Seasons-1' });
                setDisplayScaleUpOptions(!disallowScaleUpThroughXRegex.test(throughX?.value));
                break;
            case 'dateRange':
                setHeader(<h1>Query - Find Players Meeting Criteria in a Date Range</h1>)
                setDisplayScaleUpOptions(true);
                break;
            case 'players':
                setHeader(<h1>Query - Find Specific Players</h1>)
                setDisplayScaleUpOptions(true);
                disStatIds.push(2);
                break;
            case 'contracts':
                setHeader(<h1>Query - Find Contracts</h1>)
                setDisplayScaleUpOptions(false);
                disStatIds.push(554, 555, 733, 734);
                break;
            default:
                setHeader(<h1>No header</h1>);
                disStatIds.push(2);
                break;
        }

        setDisplayStats(await createStatListFromTabName(curTabName));

        let reportButtons = document.getElementById('reportForm').getElementsByClassName('custom-control-input');
        for (let i = 0; i < reportButtons.length; i++) {
            if (reportButtons[i].checked) {
                reportButtons[i].checked = false;
            }
        }
        let rbn = document.getElementById('custom-inline-radio-custom');
        rbn.click();

        setDataRowCount(0);
    }

    async function convertCriteriaToNote(criteria) {
        let statInfo = await columnManager.getSingleColumnById(criteria.stat);
        let criteriaValues = [];
        if (statInfo.formInputs.asCurrency === true) {
            criteriaValues.push('$' + criteria.value)
        } else if (statInfo.formInputs.inputType === 'DateRange') {
            criteria.operator = ''
            criteria.note = null
            criteriaValues.push('From: ' + criteria.value.start + ' To: ' + criteria.value.end)
        } else {
            criteria.value.split(',').forEach(critVal => {
                criteriaValues.push(statInfo?.formInputs?.selectOptions?.length > 0
                    ? statInfo.formInputs.selectOptions.find(selOpt => selOpt.value === critVal).label
                    : criteria.value);
            })
        }
        let noteObj = criteria.note;
        if (['dateRange'].includes(tabName)) {
            noteObj = noteObj.replace('Career To Date', 'Date Range')
        }
        return `${statInfo?.title} ${criteria.operator} ${criteriaValues.join(',')} ${noteObj ? 'in ' + noteObj : ''}`;
    }

    async function getNotesFromForm(params) {
        let noteObj = {};
        if (['season', 'consecutive', 'throughX', 'dateRange'].includes(tabName)) {
            if (tabName === 'consecutive') {
                noteObj.timePeriodNotes = [];
                for (let i = consMinSeason; i <= consMaxSeason; i++) {
                    noteObj.timePeriodNotes.push(i);
                }
            } else if (tabName === 'dateRange') {
                noteObj.timePeriodNotes = dateRanges.map(dateRange => {
                    return new Intl.DateTimeFormat('en-US').format(dateRange.startDate) + '-' + new Intl.DateTimeFormat('en-US').format(dateRange.endDate);
                });
            } else {
                noteObj.timePeriodNotes = selectedSeasons;
            }
            noteObj.playerCriteriaNotes = await Promise.all(playerCriteria.map(convertCriteriaToNote));
        }
        if (['players'].includes(tabName)) {
            noteObj.playerSeasonNotes = playerSeasons.map(playerSeason => {
                return `${allPlayers.find(player => player.id === playerSeason.playerId)?.name} in platform season: ${playerSeason.platformYear}`;
            });
        } else if (playerSeasons.length > 0 && tabName !== 'contracts') {
            noteObj.playerSeasonNotes = playerSeasons.map(playerSeason => {
                return `Including ${allPlayers.find(player => player.id === playerSeason.playerId)?.name} in platform season: ${playerSeason.platformYear}`;
            });
        }
        if (!['players'].includes(tabName)) {
            noteObj.numericalCriteriaNotes = params.numCritNotes;
        }
        if (['consecutive', 'throughX'].includes(tabName)) {
            noteObj.consecutiveYearsNote = tabName === 'consecutive'
                ? `In ${consecutiveYears} consecutive years`
                : '';

            let throughXCategory = throughX.category;
            let throughXSubCategory = throughX.value?.split('-')[0];
            let throughXValue = throughX.value?.split('-')[1];
            let throughXCategoryNote = throughXValue ? throughXSubCategory : throughXCategory;
            let throughXValueNote = throughXSubCategory === 'MLS' ? throughXValue.substring(0, 1) + '+' : throughXValue;
            noteObj.throughXStatNote = 'Through ';
            if (throughX.category === 'Any') {
                noteObj.throughXStatNote += 'any season';
            } else if (throughX.category === 'Stat') {
                noteObj.throughXStatNote += `the players first ${throughXValue} ${throughXCategoryNote}`;
            } else {
                noteObj.throughXStatNote += `the players ${throughXCategoryNote} ${throughXValueNote} season`;
            }
        }
        let noteArr = params.displayStatNotes;
        if (['dateRange'].includes(tabName)) {
            noteArr = noteArr.map(function (item, index) {
                return item.replace('Career To Date', 'Date Range')
            });
        }
        noteObj.displayStatNotes = noteArr;
        setUsingScaleUp(scaleUpFilters || scaleUpDisplayStats);
        noteObj.scaleUpNotes = `Query filters based on ${scaleUpFilters ? '' : 'not '}scaled up data. ` +
            `Results display ${scaleUpDisplayStats ? '' : 'not '}scaled up data${scaleUpDisplayStats === 2 ? ' nested under not scaled up data' : ''}.`;

        return noteObj;
    }

    async function getData() {
        setLoading(true);
        setErrorMsg(null);
        setData([]);
        setDataRowCount(0);
        let start = new Date();

        let params = await transformInputsToParams();
        setCols(params.columns);
        setNotes(await getNotesFromForm(params));

        (async () => {
            console.log(`Starting call to get data at: ${Date.now() - start}`);
            try {
                let res;
                if (tabName === 'contracts') {
                    res = await GetQueryToolContractsData(params.numericalCriteria, params.displayStats);
                } else {
                    res = await GetQueryToolPlayerData(params.seasons, params.playerCriteria, params.numericalCriteria, params.displayStats,
                        params.consecutiveYears, params.throughX, params.dateRanges, params.playerSeasons,
                        params.scaleUpFilters, params.scaleUpDisplayStats, params.aggregationOptions);
                }
                console.log(`finished getting data at: ${Date.now() - start}`);

                if (res.exceptionMsg) {
                    setErrorMsg(res.exceptionMsg);
                } else if (res.data.length === 0) {
                    setErrorMsg('No Data');
                } else {
                    let missingColumns = [];
                    params.columns.forEach(column => {
                        if (!column.isCalculator) {
                            let resCol = res.columns.find(resCol => resCol.displayName === column.field);
                            if (resCol)
                                column.field = resCol.displayNumber.toString();
                            else
                                missingColumns.push(column.title ?? column.field);
                        }
                    })
                    if (missingColumns.length > 0) {
                        setErrorMsg(`The following stat(s) are not currently available for this report: ${missingColumns.join(', ')}`);
                        setLoading(false);
                        return;
                    }
                    setErrorMsg('');
                    let rowNumCol = await columnManager.getSingleColumnById(10000); // get row number column config
                    params.columns.unshift(rowNumCol);  // insert row# column as first column

                    // Set calculator Columns data
                    let columnsToCalculate = {};
                    let maxStatIndex = Math.max(...Object.keys(res.data[0]).filter(key => !isNaN(key)));

                    params.columns
                        .filter(col => col.isCalculator)
                        .forEach((col, calculatorColumnIndex) => {
                            col.field = (maxStatIndex + calculatorColumnIndex + 1).toString();
                            columnsToCalculate[maxStatIndex + calculatorColumnIndex + 1] = {}
                            columnsToCalculate[maxStatIndex + calculatorColumnIndex + 1].statsToCombine =
                                col.statsToCombine.map(stat => params.columns.find(paramCol => paramCol.statIndex === stat.index));
                            columnsToCalculate[maxStatIndex + calculatorColumnIndex + 1].calculatorOperators = col.calculatorOperators;
                        });

                    Object.keys(columnsToCalculate).forEach(key => {
                        let columnToCalculate = columnsToCalculate[key];
                        res.data.forEach(row => {
                            let calculatedValue = columnToCalculate.statsToCombine.reduce((acc, curStat, index) => {
                                let statValue = row[curStat.field];
                                if (isNaN(statValue))
                                    statValue = 0;
                                else
                                    statValue = parseFloat(statValue);

                                if (index === 0) {
                                    return statValue;
                                } else {
                                    switch (columnToCalculate.calculatorOperators[index]) {
                                        case '+':
                                            return acc + statValue;
                                        case '-':
                                            return acc - statValue;
                                        case '*':
                                            return acc * statValue;
                                        case '/':
                                            return acc / statValue;
                                        // default:
                                        //     return acc + statValue; // default to addition
                                    }
                                }
                            }, 0)
                            row[key] = calculatedValue.toString();
                        });
                    });

                    setCols(params.columns);

                    setData(res.data);
                    setDataRowCount(res.data.filter(row => row.PlayerId > 0 || row.ContractId > 0).length);
                }
                
                setLoading(false);
                
                console.log(`finished setting data at: ${Date.now() - start}`);
            } catch (err) {
                setErrorMsg('Something went wrong getting the data. Check to make sure that you have all filters selected and try again. If you continue to get this message please contact support.')
                setLoading(false);
            }
        })();
    }

    /**
     * Parses the json from the API and sets the front end based on the loaded report.
     * @param {any} reportJson The report loaded as json from the API.
     */
    async function loadReportFromJson(reportJson) {
        let report = JSON.parse(reportJson);
        setTabName(report.tabName ?? 'season');

        function isArbCategory(category) {
            return ['MLS', 'Arb'].includes(category);
        }

        let throughXCategory = report.throughX
            ? isArbCategory(report.throughX.split('-')[0])
                ? 'Arb'
                : report.throughX.split('-')[0] === 'Age'
                    ? 'Age'
                    : 'Stat'
            : 'Any';

        switch (report.tabName) {
            case 'consecutive':
                setConsMinSeason(Math.min(...report.seasons));
                setConsMaxSeason(Math.max(...report.seasons));
                setConsecutiveYears(report.consecutiveYears);
                setThroughX({ category: throughXCategory, value: report.throughX })
                break;
            case 'throughX':
                setSelectedSeasons(report.seasons);
                setThroughX({ category: throughXCategory, value: report.throughX })
                break;
            case 'dateRange':
                let tempDateRanges = [];
                report.dateRange.split('&&').forEach(dateRange => {
                    tempDateRanges.push({
                        startDate: new Date(dateRange.split('-')[0]),
                        endDate: new Date(dateRange.split('-')[1]),
                    });
                })
                setDateRanges(tempDateRanges);
                break;
            default:
                setSelectedSeasons(report.seasons);
                break;
        }

        if (report.playerSeasons)
            setPlayerSeasons(report.playerSeasons);

        setPlayerCriteria(report.playerCriteria);
        let reportNumericalCriteria = await Promise.all(report.numericalCriteria.map(async (stat) => {
            stat.column = await columnManager.getSingleColumnById(stat.stat);
            stat.iswFilters = await deserializeFilterString(stat.iswFilter ? `ISW ${stat.iswFilter}` : '');
            stat.igwFilters = await deserializeFilterString(stat.igwFilter ? `IGW ${stat.igwFilter}` : '');
            return stat;
        }));
        setNumericalCriteria(reportNumericalCriteria);
        let reportDisplayStats = await Promise.all(report.displayStats.map(async (stat) => {
            stat.column = await columnManager.getSingleColumnById(stat.stat);
            stat.iswFilters = await deserializeFilterString(stat.iswFilter ? `ISW ${stat.iswFilter}` : '');
            stat.igwFilters = await deserializeFilterString(stat.igwFilter ? `IGW ${stat.igwFilter}` : '');
            return stat;
        }));

        let reportDisplayStatsWithCalculators = reportDisplayStats.map(stat => {
            if (stat.isCalculator) {
                stat.calculatorStats = stat.calculatorStats.map(calculatorStat => {
                    return reportDisplayStats.find(disStat => disStat.index === calculatorStat.index);
                });
                stat.calculatorColumn = columnManager.combineColumns(stat.calculatorStats, stat.calculatorOperators);
            }
            return stat;
        })
        setDisplayStats(reportDisplayStatsWithCalculators);
        setScaleUpFilters(report.scaleUpFilters);
        setScaleUpDisplayStats(report.scaleUpDisplayStats);
        setAggregationOptions(report.aggregationOptions);
    }

    /**
     * Uses transformInputsToObj to get an object containing the inputs from the front end,
     * then modifies the object to be used for saving.
     */
    async function transformInputsToReportQuery() {
        let reportQueryObj = await transformInputsToObj();

        reportQueryObj.tabName = tabName;

        reportQueryObj.playerSeasons = playerSeasons.map(playerSeason => {
            return playerSeason;
        });

        reportQueryObj.displayStats = reportQueryObj.displayStats.disStats
            .map(curDisStat => {
                curDisStat.iswFilter = curDisStat.iswFilter?.replaceAll('ISW ', '');
                curDisStat.igwFilter = curDisStat.igwFilter?.replaceAll('IGW ', '');
                return curDisStat;
            });
        return reportQueryObj;
    }

    function reformatDateStringNumCrit(dateStr) {
        if (!dateStr) {
            return ''
        }

        let parts = []
        parts = dateStr.split('-')
        return parts[1] + '/' + parts[2] + '/' + parts[0]
    }

    /**
     * Uses transformInputsToObj to get an object containing the inputs from the front end,
     * then modifies the object to be used making the API call.
     */
    async function transformInputsToParams() {
        let paramObj = await transformInputsToObj();

        paramObj.seasons = paramObj.seasons?.join(',');
        paramObj.playerCriteria = paramObj.playerCriteria
            .map(playerCrit => playerCrit.stat + playerCrit.operator + playerCrit.value)
            .join('&&');
        paramObj.numCritNotes = await Promise.all(paramObj.numericalCriteria.map(convertCriteriaToNote));
        paramObj.numericalCriteria = (await Promise.all(paramObj.numericalCriteria
            .map(async curNumCrit => {
                let param = ''
                let statFormInputs = (await columnManager.getSingleColumnById(curNumCrit.stat)).formInputs;
                if (statFormInputs.inputType === 'DateRange') {
                    param =
                        curNumCrit.stat + '=1 in ' +
                        reformatDateStringNumCrit(curNumCrit.value.start) + '-' +
                        reformatDateStringNumCrit(curNumCrit.value.end);
                } else if (statFormInputs.asCurrency === true) {
                    param = curNumCrit.stat + curNumCrit.operator + curNumCrit.value.replaceAll(',', '').replaceAll('$', '');    // replace method removes non-numerics, specifically commas from currency;
                } else {
                    param = curNumCrit.stat + curNumCrit.operator + curNumCrit.value;
                }
                if (tabName !== 'contracts') {
                    param += curNumCrit.timePeriod ? ` in ${curNumCrit.timePeriod}` : '';
                    param += curNumCrit.iswFilter ? ` ${curNumCrit.iswFilter}` : '';
                    param += curNumCrit.igwFilter ? ` ${curNumCrit.igwFilter}` : '';
                }
                return param;
            })))
            .join('&&');
        paramObj.displayStatNotes = paramObj.displayStats.notes;
        paramObj.columns = paramObj.displayStats.columns;
        paramObj.displayStats = paramObj.displayStats.paramInputs
            .filter(disStat => !disStat.includes('null'))
            .join('&&');

        return paramObj;
    }

    /**
     * Generates an object that holds all inputs from the form to be used for saving or passing to the API. 
     */
    async function transformInputsToObj() {
        let inputObj = {};

        if (tabName === 'consecutive') {
            let consSeasonsArr = [];
            for (let i = consMinSeason; i <= consMaxSeason; i++) {
                consSeasonsArr.push(i + '');
            }
            inputObj.seasons = consSeasonsArr;
            inputObj.consecutiveYears = Number(consecutiveYears);
        } else if (tabName === 'dateRange') {
            inputObj.dateRanges = dateRanges
                .map(dateRange => {
                    return dateRange.startDate.toLocaleString('en-US', { day: 'numeric', month: 'numeric', year: 'numeric' })
                        + '-'
                        + dateRange.endDate.toLocaleString('en-US', { day: 'numeric', month: 'numeric', year: 'numeric' });
                })
                .join('&&');
        } else {
            inputObj.seasons = selectedSeasons;
        }

        inputObj.playerSeasons = playerSeasons
            .filter(playerSeason => playerSeason != null) // Filter out null values
            .map(playerSeason => {
                return playerSeason.playerId + '-' + playerSeason.platformYear;
            })
            .join('&&');

        if (['consecutive', 'throughX'].includes(tabName)) {

            inputObj.throughX = throughX.value;
        }

        inputObj.playerCriteria = playerCriteria
            .filter(crit => crit.stat !== '' && crit.stat != null);
        inputObj.numericalCriteria = await createNumCritInputArr();
        inputObj.displayStats = await createDisStatInputObj();
        inputObj.scaleUpFilters = scaleUpFilters;
        inputObj.scaleUpDisplayStats = scaleUpDisplayStats;
        inputObj.aggregationOptions = aggregationOptions;

        return inputObj;
    }

    /**
     * Generates an array of numerical criteria objects that hold general information about each numerical criteria row
     */
    async function createNumCritInputArr() {
        let numCritInputArr = [];

        for (const curNumCrit of numericalCriteria) {
            if (curNumCrit.stat === '' || curNumCrit.stat == null) {
                return;
            }
            let numCritObj = Object.assign({}, curNumCrit);
            let statFormInputs = (await columnManager.getSingleColumnById(numCritObj.stat)).formInputs;
            if (statFormInputs.inputType === 'DateRange') {
                numCritObj.operator = '=';
                numCritObj.timePeriod = '';
            }

            numCritObj.iswFilter = serializeFilterTree(curNumCrit.iswFilters || []);
            numCritObj.igwFilter = serializeFilterTree(curNumCrit.igwFilters || []);

            const iswTitle = await deriveFilterLabel(curNumCrit.iswFilters || [], 'Title');
            const igwTitle = await deriveFilterLabel(curNumCrit.igwFilters || [], 'Title');
            let filterNote = (iswTitle + ' ' + igwTitle).trim();

            let curNote = tabName !== 'contracts' ? curNumCrit.timePeriod ?? '' : '';
            if (filterNote) {
                curNote += ` ${filterNote
                    .replaceAll('&', ' AND ')
                    .replaceAll('|', ' OR ')
                    .replaceAll('IGW ', 'in games where ')
                    .replaceAll('ISW ', 'where ')}`;
            }
            numCritObj.note = curNote.trim();

            numCritInputArr.push(numCritObj);
        }

        return numCritInputArr;
    }

    /**
     * Generates an object that holds information based on the display stats section.
     * Object properties include:
     * disStats: Array of objects for each display stat row
     * paramInputs: Array of parameters that can be joined to make up the full parameter of display stats
     * notes: Array of notes to be displayed describing each type of stat
     * columns: Array of tabulator columns to be used in tabulator for displaying the stats
     */
    async function createDisStatInputObj() {
        let noFilterStats = [1, 2];
        let notes = [];
        let paramInputs = [];
        let columns = [];
        let disStats = [];
        for (const curDisStat of displayStats) {
            if (!curDisStat.isCalculator && (curDisStat.stat === '' || curDisStat.stat == null)) {
                continue;
            }
            let column = Object.assign(
                {},
                curDisStat.calculatorColumn,
                allColumns.find(col => col.id === curDisStat.stat),
                { statIndex: curDisStat.index }
            );
            let disStatObj = Object.assign({}, curDisStat);
            let paramInput = curDisStat.stat + '';
            let curNote = disStatObj.isCalculator
                ? disStatObj.calculatorColumn.getCalculatorFormula(true)
                : '';

            if (!noFilterStats.includes(curDisStat.Stat) && !disStatObj.isCalculator) {
                disStatObj.iswFilter = serializeFilterTree(curDisStat.iswFilters || []);
                disStatObj.igwFilter = serializeFilterTree(curDisStat.igwFilters || []);

                const iswTitle = await deriveFilterLabel(curDisStat.iswFilters || [], 'Title');
                const igwTitle = await deriveFilterLabel(curDisStat.igwFilters || [], 'Title');
                let filterNote = (iswTitle + ' ' + igwTitle).trim();

                const iswField = await deriveFilterLabel(curDisStat.iswFilters || [], 'Field');
                const igwField = await deriveFilterLabel(curDisStat.igwFilters || [], 'Field');
                let filterField = (iswField + ' ' + igwField).trim();

                if (curDisStat.timePeriod && tabName !== 'contracts') {
                    if (tabName === 'dateRange') {
                        curNote = 'Date Range';
                    }
                    else {
                        curNote = curDisStat.timePeriod;
                    }
                    paramInput += ` in ${curDisStat.timePeriod}`;
                    column.field = `${curDisStat.timePeriod} ${column.field}`;
                }

                switch (column.statPosType) {
                    case 'b':
                        column.field += ' as Batter';
                        break;
                    case 'p':
                        column.field += ' as Pitcher';
                        break;
                    case 'f':
                        column.field += ' as Fielder';
                        break;
                    default:
                        column.field += '';
                        break;
                }

                if (filterNote) {
                    paramInput += ` ${disStatObj.iswFilter} ${disStatObj.igwFilter}`
                    curNote += ` ${filterNote
                        .replaceAll('&', ' AND ')
                        .replaceAll('|', ' OR ')
                        .replaceAll('IGW ', 'in games where ')
                        .replaceAll('ISW ', 'where ')}`;
                    column.field += ` ${filterField
                        .replaceAll('&', ' and ')
                        .replaceAll('|', ' or ')
                        .replaceAll('IGW ', 'in games where ')
                        .replaceAll('ISW ', 'where ')}`;
                }

                paramInput = paramInput.trim();
                curNote = curNote.trim();
                column.field = column.field.trim();
            }

            if (column.statPosType) {
                let subScript = document.createElement('sub');
                subScript.innerHTML = column.statPosType;
                column.title += subScript.outerHTML;
            }

            paramInputs.push(paramInput);
            if (curNote) {
                let noteIndex = notes.findIndex(note => note === curNote);
                column.titleFormatter = function (cell, formatterParams, onRendered) {
                    cell.getElement().style.whiteSpace = "pre-wrap";
                    return cell.getValue();
                };
                let superScript = document.createElement('sup');
                if (!~noteIndex) {
                    superScript.innerHTML = notes.length + 1;
                    notes.push(curNote);
                } else {
                    superScript.innerHTML = noteIndex + 1;
                }
                column.title += superScript.outerHTML;
                column.headerTooltip = curNote;
            }
            columns.push(column);
            disStats.push(disStatObj);
        }

        return {
            disStats: disStats,
            paramInputs: paramInputs,
            notes: notes,
            columns: columns,
        };
    }

    const currentSeason = (new Date()).getMonth() >= 3 ? (new Date()).getFullYear() : (new Date()).getFullYear() - 1;
    let seasons = [];
    for (let season = currentSeason; season >= 2002; season--) {
        seasons.push(season + '');
    }

    let disallowScaleUpThroughXRegex = new RegExp(/(G|GS|PA|IP|RAPP)-\d+/);

    useEffect(() => {
        if (disallowScaleUpThroughXRegex.test(throughX?.value)) {
            setScaleUpDisplayStats(0);
            setScaleUpFilters(false);
        }
        setDisplayScaleUpOptions(!disallowScaleUpThroughXRegex.test(throughX?.value));
    }, [throughX])

    useEffect(() => {
        if (scaleUpDisplayStats === 2) {
            setDisplayAggregationOptions(false);
            setAggregationOptions(aggregationValues.none);
        }
        else {
            setDisplayAggregationOptions(true);
        }
    }, [scaleUpDisplayStats])

    async function updateReport(reportName, newTabName) {
        setDisplayStats(await createStatListFromColIds([1]));
        newTabName = newTabName ?? tabName;
        let statList = [];
        switch (reportName) {
            case 'DefaultBatter':
                statList = await createStatListFromColIds(defaultGroups[0]);
                break;
            case 'DefaultCatcher':
                statList = await createStatListFromColIds(defaultGroups[1]);
                break;
            case 'DefaultStartingPitcher':
                statList = await createStatListFromColIds(defaultGroups[2]);
                break;
            case 'DefaultReliefPitcher':
                statList = await createStatListFromColIds(defaultGroups[3]);
                break;
            case 'DefaultContracts':
                statList = await createStatListFromColIds(defaultGroups[4]);
                break;
            case 'Custom':
                statList = await createStatListFromColIds(
                    newTabName === 'contracts'
                        ? [1, 554, 555, 733, 734]
                        : ['throughX', 'dateRange'].includes(newTabName) ? [1] : [1, 2]); //1 = Player & 2 = Year
                break;
            case 'CopyFromAbove':
                let baseColumns = await createStatListFromTabName(newTabName);
                let numCritColumns = await createStatListFromNumCrits(await createNumCritInputArr(), timePeriodNotSelectable(newTabName) ? 1 : 2);
                statList = baseColumns.concat(numCritColumns);
                break;
            default:
                break;
        }
        setDisplayStats(statList);
    }

    async function createStatListFromColIds(colIds) {
        let disStatSelectableStatsByGroup = selectableStatsByGroup.map(group => {
            return group.filter(stat => stat.formInputs.isDisStat);
        });
        return await Promise.all(colIds.map(async (colId, index) => {
            let column = await columnManager.getSingleColumnById(colId);
            let statObj = {
                column: column,
                stat: colIds[index],
                index: index,
                timePeriod: timePeriodNotSelectable()
                    ? 'Career To Date'
                    : disStatSelectableStatsByGroup.flat().find(c => c.key == colId)
                        ? disStatSelectableStatsByGroup.flat().find(c => c.key == colId).formInputs.defaultTimePeriod
                        : 'Platform Year'
            }
            return statObj;
        }));
    }
    async function createStatListFromTabName(curTabName) {
        let colIds = [1];
        switch (curTabName) {
            case 'season':
            case 'consecutive':
            case 'players':
            default:
                colIds.push(2);
                break;
            case 'contracts':
                colIds.push(554, 555, 733, 734);
                break;
        }

        let disStatSelectableStatsByGroup = selectableStatsByGroup.map(group => {
            return group.filter(stat => stat.formInputs.isDisStat);
        });
        return await Promise.all(colIds.map(async (colId, index) => {
            let column = await columnManager.getSingleColumnById(colId);
            let statObj = {
                column: column,
                stat: colIds[index],
                index: index,
                timePeriod: timePeriodNotSelectable(curTabName)
                    ? 'Career To Date'
                    : disStatSelectableStatsByGroup.flat().find(c => c.key == colId)
                        ? disStatSelectableStatsByGroup.flat().find(c => c.key == colId).formInputs.defaultTimePeriod
                        : 'Platform Year'
            }
            return statObj;
        }));
    }

    async function createStatListFromNumCrits(numCrits, startIndex) {
        return await Promise.all(numCrits.map(async (numCrit, index) => {
            let column = await columnManager.getSingleColumnById(numCrit.stat);
            return {
                column: column,
                stat: numCrit.stat,
                index: startIndex + index,
                timePeriod: timePeriodNotSelectable() ? 'Career To Date' : numCrit.timePeriod,
                iswFilter: numCrit.iswFilter,
                igwFilter: numCrit.igwFilter,
                iswFilters: await deserializeFilterString(numCrit.iswFilter ? `ISW ${numCrit.iswFilter}` : ''),
                igwFilters: await deserializeFilterString(numCrit.igwFilter ? `IGW ${numCrit.igwFilter}` : ''),
            };
        }));
    }

    function timePeriodNotSelectable(newTabName) {
        return (newTabName == null && ['throughX', 'dateRange', 'contracts'].includes(tabName))
            || ['throughX', 'dateRange', 'contracts'].includes(newTabName);
    }

    return (
        <div className='query-tool'>
            <Navbar bg='light' variant='light' className='queryNav'>
                <Nav>
                    <Nav.Link
                        className={tabName === 'season' ? 'nav-selected' : null}
                        href='#season'
                        onClick={() => reset('season')}
                    >
                        {
                            tabName === 'season' &&
                            <span style={{ transform: 'translateX(20px) rotate(135deg)' }}></span>
                        }
                        Season
                    </Nav.Link>
                    <Nav.Link
                        className={tabName === 'consecutive' ? 'nav-selected' : null}
                        href='#consecutive'
                        onClick={() => reset('consecutive')}
                    >
                        {
                            tabName === 'consecutive' &&
                            <span style={{ transform: 'translateX(34px) rotate(135deg)' }}></span>
                        }
                        Consecutive
                    </Nav.Link>
                    <Nav.Link
                        className={tabName === 'throughX' ? 'nav-selected' : null}
                        href='#throughX'
                        onClick={() => reset('throughX')}
                    >
                        {
                            tabName === 'throughX' &&
                            <span style={{ transform: 'translateX(30px) rotate(135deg)' }}></span>
                        }
                        Through X
                    </Nav.Link>
                    <Nav.Link
                        className={tabName === 'dateRange' ? 'nav-selected' : null}
                        href='#dateRange'
                        onClick={() => reset('dateRange')}
                    >
                        {
                            tabName === 'dateRange' &&
                            <span style={{ transform: 'translateX(35px) rotate(135deg)' }}></span>
                        }
                        Date Range
                    </Nav.Link>
                    <Nav.Link
                        className={tabName === 'players' ? 'nav-selected' : null}
                        href='#players'
                        onClick={() => reset('players')}
                    >
                        {
                            tabName === 'players' &&
                            <span style={{ transform: 'translateX(50px) rotate(135deg)' }}></span>
                        }
                        Player Selection
                    </Nav.Link>
                    <Nav.Link
                        className={tabName === 'contracts' ? 'nav-selected' : null}
                        href='#contracts'
                        onClick={() => reset('contracts')}
                    >
                        {
                            tabName === 'contracts' &&
                            <span style={{ transform: 'translateX(30px) rotate(135deg)' }}></span>
                        }
                        Contracts
                    </Nav.Link>
                </Nav>
            </Navbar>
            <br />

            <div className='body'>
                <AdvancedFilters
                    selectableStatsByGroup={selectableStatsByGroup.map(group => {
                        return group.filter(stat => stat.formInputs.isIGWFilter || stat.formInputs.isISWFilter);
                    })}
                    selectableStatGroups={selectableStatGroups.filter(group => {
                        return tabName !== 'contracts' || [43, 45, 46].includes(group.id);
                    })}
                    tabName={tabName}
                    setHideFilters={setHideFilters}
                    onApplyToAll={handleApplyToAll}
                />
                <SaveLoadButtons
                    header={header}
                    page={'QueryTool'}
                    resetFunction={() => reset(tabName)}
                    getReportFunction={transformInputsToReportQuery}
                    loadReportFunction={(report) => loadReportFromJson(report)}
                />
                <br />
                {
                    ['season', 'consecutive', 'throughX'].includes(tabName) &&
                    <SeasonSelect
                        tabName={tabName}
                        seasons={seasons}
                        selectedSeasons={selectedSeasons}
                        setSelectedSeasons={setSelectedSeasons}
                        curSeason={currentSeason}
                        minSeason={consMinSeason}
                        setMinSeason={setConsMinSeason}
                        maxSeason={consMaxSeason}
                        setMaxSeason={setConsMaxSeason}
                    />
                }
                {
                    tabName === 'dateRange' &&
                    <DateRangeQuery
                        dateRanges={dateRanges}
                        setDateRanges={setDateRanges}
                    />
                }
                {
                    ['season', 'players'].includes(tabName) &&
                    <PlayerQuery
                        tabName={tabName}
                        allPlayers={allPlayers}
                        playerSeasons={playerSeasons}
                        setPlayerSeasons={setPlayerSeasons}
                    />
                }
                {
                    ['season', 'consecutive', 'throughX', 'dateRange'].includes(tabName) &&
                    <PlayerCriteria
                        tabName={tabName}
                        playerCriteria={playerCriteria}
                        setPlayerCriteria={setPlayerCriteria}
                        allColumns={allColumns}
                        selectableStatsByGroup={selectableStatsByGroup.map(group => {
                            return group.filter(stat => stat.formInputs.isPlayerCrit);
                        })}
                        selectableStatGroups={selectableStatGroups}
                        hideFilters={hideFilters}
                        timePeriodNotSelectable={timePeriodNotSelectable()}
                    />
                }
                {
                    ['season', 'consecutive', 'throughX', 'dateRange', 'contracts'].includes(tabName) &&
                    <NumericalCriteria
                        tabName={tabName}
                        numericalCriteria={numericalCriteria}
                        setNumericalCriteria={setNumericalCriteria}
                        allColumns={allColumns}
                        selectableStatsByGroup={selectableStatsByGroup.map(group => {
                            return group.filter(stat => stat.formInputs.isNumCrit);
                        })}
                        selectableStatGroups={selectableStatGroups.filter(group => {
                            return tabName !== 'contracts' || [43, 45, 46].includes(group.id);
                        })}
                        hideFilters={hideFilters}
                        timePeriodNotSelectable={timePeriodNotSelectable()}
                        displayScaleUpOptions={displayScaleUpOptions && tabName !== 'contracts'}
                        scaleUp={scaleUpFilters}
                        setScaleUp={setScaleUpFilters}
                        onFilterDrop={handleNumCritFilterDrop}
                        onFilterRemove={handleNumCritFilterRemove}
                        onLogOpChange={handleNumCritLogOpChange}
                    />
                }
                {
                    ['consecutive'].includes(tabName) &&
                    <ThroughXQuery
                        tabName={tabName}
                        throughX={throughX}
                        setThroughX={setThroughX}
                        minSeason={consMinSeason}
                        maxSeason={consMaxSeason}
                        consecutiveYears={consecutiveYears}
                        setConsecutiveYears={setConsecutiveYears}
                    />
                }
                {
                    ['throughX'].includes(tabName) &&
                    <ThroughXQuery
                        tabName={tabName}
                        throughX={throughX}
                        setThroughX={setThroughX}
                        minSeason={consMinSeason}
                        maxSeason={consMaxSeason}
                        consecutiveYears={consecutiveYears}
                        setConsecutiveYears={setConsecutiveYears}
                    />
                }
                <br />
                <DisplayStats
                    tabName={tabName}
                    displayStats={displayStats}
                    setDisplayStats={setDisplayStats}
                    selectableStatsByGroup={selectableStatsByGroup.map(group => {
                        return group.filter(stat => stat.formInputs.isDisStat);
                    })}
                    selectableStatGroups={selectableStatGroups.filter(group => {
                        return tabName !== 'contracts' || [39, 43, 44, 45, 46].includes(group.id);
                    })}
                    hideFilters={hideFilters}
                    timePeriodNotSelectable={timePeriodNotSelectable()}
                    defaultGroups={defaultGroups}
                    displayScaleUpOptions={displayScaleUpOptions && tabName !== 'contracts'}
                    scaleUp={scaleUpDisplayStats}
                    setScaleUp={setScaleUpDisplayStats}
                    displayAggregationOptions={displayAggregationOptions && tabName !== 'contracts'}
                    aggregationOptions={aggregationOptions}
                    setAggregationOptions={setAggregationOptions}
                    aggregationValues={aggregationValues}
                    updateReport={updateReport}
                    onFilterDrop={handleDisStatFilterDrop}
                    onFilterRemove={handleDisStatFilterRemove}
                    onLogOpChange={handleDisStatLogOpChange}
                />
                <br />
                <Button variant='success' onClick={() => getData()}>Submit</Button>
                <br />
                <br />
                {
                    loading &&
                    <div>
                        <br />
                        <br />
                        <i className='fa fa-spinner fa-spin loading-icon'></i>
                        <br />
                    </div>
                }
                {
                    errorMsg &&
                    <React.Fragment>
                        <p style={{ color: 'rgb(220, 53, 69)', textAlign: 'center' }}>{errorMsg}</p>
                        <br />
                        <br />
                    </React.Fragment>
                }
                {
                    data && data.length > 0 &&
                    <React.Fragment>
                        <ExcelExporter reportName={'QueryTool'} data={data} columns={cols} notes={notes} />
                        {
                            false &&
                            <React.Fragment>
                                <PDFExporter fileName={'QueryTool'} /*scrollPos={scrollPos} columns={cols}*/ />
                                <Button id='PdfExportBtn'>Other PDF</Button>
                            </React.Fragment>
                        }
                        <div id='notes'>
                            <h4>Notes</h4>
                            <p>
                                There are <b>{dataRowCount}</b> rows.
                            </p>
                            {
                                notes.timePeriodNotes &&
                                <React.Fragment>
                                    <p>
                                        <b>The following { tabName === 'dateRange' ? 'date ranges' : 'seasons' } have been selected:</b>
                                    </p>
                                    <p style={{ wordWrap: 'break-word' }}>
                                        {
                                            notes.timePeriodNotes.join(', ')
                                        }
                                    </p>
                                </React.Fragment>
                            }
                            {
                                notes.playerSeasonNotes &&
                                <React.Fragment>
                                    <p>
                                        <b>The following players have been selected:</b>
                                    </p>
                                    {
                                        notes.playerSeasonNotes.map(playerSeason => {
                                            return <p>{playerSeason}</p>
                                        })
                                    }
                                </React.Fragment>
                            }
                            {
                                notes.playerCriteriaNotes &&
                                <React.Fragment>
                                    <p>
                                        <b>The following player criteria have been selected:</b>
                                    </p>
                                    {
                                        notes.playerCriteriaNotes.map(playerCriteriaNote => {
                                            return <p>{playerCriteriaNote}</p>
                                        })
                                    }
                                </React.Fragment>
                            }
                            {
                                notes.numericalCriteriaNotes &&
                                <React.Fragment>
                                    <p>
                                        <b>The following numerical criteria have been selected:</b>
                                    </p>
                                    {
                                        notes.numericalCriteriaNotes.map(numericalCriteriaNote => {
                                            return <p>{numericalCriteriaNote}</p>
                                        })
                                    }
                                </React.Fragment>
                            }
                            {
                                notes.throughXStatNote &&
                                <React.Fragment>
                                    <p>
                                        <b>The following {tabName === 'consecutive' ? 'consecutive' : 'through x'} criteria have been selected:</b>
                                    </p>
                                    <p>{notes.consecutiveYearsNote}</p>
                                    <p>{notes.throughXStatNote}</p>
                                </React.Fragment>
                            }
                            {
                                notes.displayStatNotes &&
                                <React.Fragment>
                                    <p>
                                        <b>Annotation Key:</b>
                                    </p>
                                    {
                                        notes.displayStatNotes.map((note, index) => {
                                            return <p>{(index + 1) + ' = ' + note}</p>
                                        })
                                    }
                                </React.Fragment>
                            }
                            {
                                notes.scaleUpNotes &&
                                <React.Fragment>
                                    <p style={{ backgroundColor: usingScaleUp ? '#FED8B1' : 'white' }}>
                                        {notes.scaleUpNotes}
                                    </p>
                                </React.Fragment>
                            }
                        </div>
                        <div
                            className='pdfSection'
                            style={{
                                width: data
                                    ? (cols.reduce((acc, cur) => acc + ((cur.width) > 20
                                        ? (cur.width)
                                        : 20), 0)) + 4
                                    : '100%'
                            }}
                        >
                            <div className='pdfChunk'>
                                <TabulatorTable
                                    cols={cols}
                                    data={data}
                                    movableColumns={true}
                                    pdfExport={false}
                                    initSort={[{
                                        column: cols.find(col => col.formInputs?.field === 'Player').field,
                                        dir: 'asc'
                                    }]}
                                    dataTreeElementColumn='Team'
                                    paginationSize={150}
                                />
                            </div>
                        </div>
                    </React.Fragment>
                }
            </div>
        </div>
    )
}

export default QueryTool;

function SeasonSelect(props) {
    return (
        <React.Fragment>
            <h4>Select Season(s)</h4>
            <div
                id='SeasonSelect'
                style={{
                    width: '100%',
                    backgroundColor: 'lightgrey',
                    border: 'solid 1px black',
                    padding: '10px 10px 10px 25px' 
                }}
            >
                {
                    props.tabName === 'consecutive' &&
                    <div className='row'>
                        Start Season:
                        <SeasonDropDown
                            minSeason={2002}
                            maxSeason={props.maxSeason}
                            selectedSeason={props.minSeason}
                            setSeason={props.setMinSeason}
                        />
                        End Season:
                        <SeasonDropDown
                            minSeason={props.minSeason}
                            maxSeason={props.curSeason}
                            selectedSeason={props.maxSeason}
                            setSeason={props.setMaxSeason}
                        />
                    </div>
                }
                {
                    props.tabName !== 'consecutive' &&
                    <MultiSelect
                        label={'Season'}
                        list={props.seasons.map(item => { return { label: item + '', value: item + '' } })}
                        selectedItems={props.selectedSeasons}
                        setSelectedItems={(items) => props.setSelectedSeasons(items)}
                    />
                }
            </div>
        </React.Fragment>
    )
}

function ThroughXQuery(props) {
    const [header, setHeader] = useState('');
    const [radioBtnCategories, setRadioBtnCategories] = useState(props.tabName === 'consecutive'
        ? ['Any', 'Age', 'Arb']
        : ['Stat', 'Age', 'Arb']
    );
    const [statVal, setStatVal] = useState(props.throughX.category === 'Stat' ? props.throughX.value : 'Seasons-1');
    const [age, setAge] = useState(props.throughX.category === 'Age' ? props.throughX.value : 'Age-18');
    const [arbVal, setArbVal] = useState(props.throughX.category === 'Arb' ? props.throughX.value : 'Arb-1');

    function changeThroughX(category) {
        switch (category) {
            case 'Age':
                props.setThroughX({ category: 'Age', value: age });
                break;
            case 'Arb':
            case 'MLS':
                props.setThroughX({ category: 'Arb', value: arbVal });
                break;
            case 'Stat':
                props.setThroughX({ category: 'Stat', value: statVal });
                break;
            case 'Any':
                props.setThroughX({ category: 'Any' });
                break;
            default:
                break;
        }
    }

    function changeStatVal(field, value) {
        let curStat = statVal.split('-')[0];
        let curStatVal = statVal.split('-')[1];
        if (field === 'stat') {
            curStat = value;
        } else {
            curStatVal = value;
        }
        setStatVal(`${curStat}-${curStatVal}`);
    }

    useEffect(() => {
        props.setThroughX({ category: 'Age', value: age });
    }, [age])

    useEffect(() => {
        props.setThroughX({ category: 'Arb', value: arbVal });
    }, [arbVal])

    useEffect(() => {
        props.setThroughX({ category: 'Stat', value: statVal });
    }, [statVal])

    let arbDropDownOptions = [
        { value: 'Arb-1', label: 'First-time arb eligible' },
        { value: 'Arb-2', label: '2nd-time arb eligible' },
        { value: 'Arb-3', label: '3rd-time arb eligible' },
        { value: 'Arb-4', label: '4th-time arb eligible' },
        { value: 'MLS-1', label: 'MLS 1' },
        { value: 'MLS-1.171', label: 'MLS 1+' },
        { value: 'MLS-2.171', label: 'MLS 2+' },
        { value: 'MLS-3.171', label: 'MLS 3+' },
        { value: 'MLS-4.171', label: 'MLS 4+' },
        { value: 'MLS-5.171', label: 'MLS 5+' },
        { value: 'MLS-6.171', label: 'MLS 6+' },
        { value: 'MLS-7.171', label: 'MLS 7+' },
        { value: 'MLS-8.171', label: 'MLS 8+' },
        { value: 'MLS-9.171', label: 'MLS 9+' },
        { value: 'MLS-10.171', label: 'MLS 10+' },
    ]

    let statDropdownOptions = [
        { label: 'Seasons', value: 'Seasons' },
        { label: 'Games', value: 'G' },
        { label: 'Games Started', value: 'GS' },
        { label: 'PA', value: 'PA' },
        { label: 'IP', value: 'IP' },
        { label: 'Relief Appearances', value: 'RAPP' },
    ]

    let radioBtns = [
        {
            category: 'Any',
            label: <div className='row'>Any time in the Player's Career</div>,
        },
        {
            category: 'Stat',
            label:
                <OverlayTrigger
                    placement={'top'}
                    overlay={
                        <Tooltip id={'tooltip'}>
                            <p>Can be less than input stat if player meets the criteria above</p>
                            <p>Through X must at least partially fall in the selected seasons</p>
                            <p>Games represents games of any type (fielding, pitching or hitting)</p>
                            <p>Players that debuted before 2002 are excluded</p>
                        </Tooltip>
                    }
                >
                    <div className='row'>
                        Through the Player's First
                        <Form.Control
                            defaultValue={props.throughX.category === 'Stat' ? Number(props.throughX.value.split('-')[1]) : 0}
                            style={{ width: '80px', marginLeft: '10px', marginRight: '10px' }}
                            type='number'
                            onChange={(event) => changeStatVal('value', event.target.value)}
                        />
                        <Form.Control
                            style={{ width: '200px', marginLeft: '10px', marginRight: '10px' }}
                            as='select'
                            onChange={(event) => changeStatVal('stat', event.target.value)}
                            defaultValue={props.throughX.category === 'Stat' && props.throughX.value.split('-')[0]}
                        >
                            {
                                statDropdownOptions.map((option, index) => (
                                    <option
                                        key={index}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))
                            }
                        </Form.Control>
                    </div>
                </OverlayTrigger>,
        },
        {
            category: 'Age',
            label:
                <div className='row'>
                    Through the Player's Age
                    <IntRangeDropdown
                        minVal={18}
                        maxVal={42}
                        selectedVal={props.throughX.category === 'Age' ? Number(props.throughX.value.split('-')[1]) : age.split('-')[1]}
                        sortOrder={'asc'}
                        setSelectedVal={(val) => setAge(`Age-${val}`)}
                    />
                    Season
                </div>,
        },
        {
            category: 'Arb',
            label:
                <div className='row'>
                    Through the Player's
                    <Form.Control
                        style={{ width: '200px', marginLeft: '10px', marginRight: '10px' }}
                        as='select'
                        value={arbVal}
                        onChange={(event) => setArbVal(event.target.value)}
                    >
                        {
                            arbDropDownOptions.map((option, index) => (
                                <option
                                    key={index}
                                    value={option.value}
                                >
                                    {option.label}
                                </option>
                            ))
                        }
                    </Form.Control>
                    Platform Season
                </div>,
        },
    ]

    useEffect(() => {
        if (props.tabName === 'consecutive') {
            setRadioBtnCategories(['Any', 'Age', 'Arb']);
            setHeader(<h4>Consecutive Criteria?</h4>);
        } else {
            setRadioBtnCategories(['Stat', 'Age', 'Arb']);
            setHeader(<h4>Through X Criteria?</h4>);
        }
    }, [props.tabName])

    return (
        <React.Fragment>
            {header}
            <div
                style={{
                    width: '100%',
                    backgroundColor: 'lightgrey',
                    border: 'solid 1px black',
                    padding: '10px'
                }}
            >
                {
                    props.tabName === 'consecutive' &&
                    <div className='row'>
                        In
                        <IntRangeDropdown
                            minVal={1}
                            maxVal={props.maxSeason - props.minSeason + 1}
                            selectedVal={props.consecutiveYears}
                            sortOrder={'asc'}
                            setSelectedVal={props.setConsecutiveYears}
                        />
                        Consecutive Years
                    </div>
                }
                {
                    radioBtnCategories.map((category, index) => {
                        const radioButton = radioBtns.find(radioBtn => radioBtn.category === category);
                        return (
                            <Form.Check
                                key={index}
                                type={'radio'}
                                name='throughX-radio'
                                id={`throughX-${category}-radio`}
                                label={radioButton.label}
                                checked={props.throughX.category === category}
                                onChange={() => changeThroughX(category)}
                            />
                        );
                    })
                }
            </div>
        </React.Fragment>
    )
}

function DateRangeQuery(props) {
    function setStartDate(index, startDate) {
        let tempDateRanges = props.dateRanges.slice(0);
        tempDateRanges[index].startDate = startDate;
        props.setDateRanges(tempDateRanges);
    }

    function setEndDate(index, endDate) {
        let tempDateRanges = props.dateRanges.slice(0);
        tempDateRanges[index].endDate = endDate;
        props.setDateRanges(tempDateRanges);
    }

    function removeDateRange(index) {
        let tempDateRanges = props.dateRanges.slice(0);
        tempDateRanges.splice(index, 1);
        props.setDateRanges(tempDateRanges);
    }

    return (
        <React.Fragment>
            <h4>Select Date Range</h4>
            <div
                style={{
                    width: '100%',
                    backgroundColor: 'lightgrey',
                    border: 'solid 1px black',
                    padding: '10px'
                }}
            >
                {
                    props.dateRanges.map((dateRange, index) => {
                        let endDate = customDateString(dateRange.endDate);
                        let startDate = customDateString(dateRange.startDate);
                        return (
                            <Row key={index}>
                                <Col xs={6} lg={5} xl={2}>
                                    <Row>
                                        <b className='label' style={{ paddingRight: '25px' }}>From: </b>
                                        <input
                                            value={startDate}
                                            type='date'
                                            min={customDateString(new Date(2002, 0, 0))}
                                            max={endDate}
                                            onChange={(e) => {
                                                if (e.target.value) { setStartDate(index, new Date(e.target.value + 'T00:00:00')) }
                                            }}
                                        />
                                    </Row>
                                </Col>
                                <Col xs={5} lg={5} xl={2}>
                                    <Row>
                                        <b className='label' style={{ padding: '0px 25px' }}>To: </b>
                                        <input
                                            value={endDate}
                                            type='date'
                                            min={startDate}
                                            max={customDateString(new Date())}
                                            onChange={(e) => {
                                                if (e.target.value) { setEndDate(index, new Date(e.target.value + 'T00:00:00')) }
                                            }}
                                        />
                                    </Row>
                                </Col>
                                <Col xs={1}>
                                    <Badge
                                        style={{ cursor: 'pointer' }}
                                        tabIndex='0'
                                        variant='danger'
                                        onClick={() => removeDateRange(index)}
                                    >
                                        X
                                    </Badge>
                                </Col>
                            </Row>
                        )
                    })
                }
                <Button variant='info' onClick={() => {
                    if (props.dateRanges.length < 9) {
                        let tempDateRanges = props.dateRanges.slice(0);
                        tempDateRanges.push({ startDate: new Date(2002, 1, 1), endDate: new Date() });
                        props.setDateRanges(tempDateRanges);
                    }
                }}>Add Date Range</Button>
            </div>
        </React.Fragment>
    )
}

function PlayerQuery(props) {
    function setPlayer(index, playerId, platformYear) {
        let tempPlayerSeasons = props.playerSeasons.slice(0);
        tempPlayerSeasons[index] = { playerId: playerId, platformYear: platformYear };
        props.setPlayerSeasons(tempPlayerSeasons);
    }

    function removeSelectedPlayer(index) {
        let tempPlayerSeasons = props.playerSeasons.slice(0);
        tempPlayerSeasons.splice(index, 1);
        props.setPlayerSeasons(tempPlayerSeasons);
    }

    return (
        <React.Fragment>
            <h4>
                {props.tabName === 'players' ? 'Select Players and Seasons' : 'Add Player-Season'}
            </h4>
            <div
                style={{
                    width: '100%',
                    backgroundColor: 'lightgrey',
                    border: 'solid 1px black',
                    padding: '10px'
                }}
            >
                {
                    props.playerSeasons.map((playerSeason, index) => {
                        return (
                            <Row>
                                <PlayerSearch
                                    key={index}
                                    players={props.allPlayers}
                                    label={'Player'}
                                    selectedPlayer={playerSeason}
                                    onSelect={(playerId, platformYear, posFlag) => setPlayer(index, playerId, platformYear)}
                                />
                                <Badge
                                    style={{ cursor: 'pointer' }}
                                    tabIndex='0'
                                    variant='danger'
                                    onClick={() => removeSelectedPlayer(index)}
                                >
                                    X
                                </Badge>
                            </Row>
                        )
                    })
                }
                <Button variant='info' onClick={() => {
                    /*if (props.playerSeasons.length < 9) {*/
                        let tempPlayerSeasons = props.playerSeasons.slice(0);
                        tempPlayerSeasons.push({ playerId: 0, platformYear: 0 })
                        props.setPlayerSeasons(tempPlayerSeasons);
                    /*}*/
                }}>Add Player</Button>
            </div>
        </React.Fragment>    
    )
}
